import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { 
  RadioButtonChecked, 
  RadioButtonUnchecked, 
  Wifi, 
  WifiOff,
  Sync,
  SyncDisabled 
} from '@mui/icons-material';
import { useWebSocket } from '../context/WebSocketProvider';

const RealtimeStatusIndicator = ({ compact = false }) => {
  const { isConnected, connectionStatus } = useWebSocket();

  if (compact) {
    return (
      <Tooltip title={`Real-time updates: ${isConnected ? 'Connected' : 'Disconnected'}`}>
        <Chip
          icon={isConnected ? <RadioButtonChecked /> : <RadioButtonUnchecked />}
          label="Live"
          color={isConnected ? 'success' : 'error'}
          variant="outlined"
          size="small"
          sx={{
            fontSize: '0.75rem',
            height: '24px',
            '& .MuiChip-icon': {
              fontSize: '14px'
            }
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        p: 1,
        borderRadius: 1,
        backgroundColor: isConnected ? 'success.light' : 'error.light',
        color: isConnected ? 'success.contrastText' : 'error.contrastText',
        opacity: 0.9
      }}
    >
      {isConnected ? <Wifi /> : <WifiOff />}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {isConnected ? 'Live Updates Active' : 'Live Updates Disconnected'}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          {isConnected 
            ? 'Registry will update automatically' 
            : 'Please refresh manually for latest data'
          }
        </Typography>
      </Box>
    </Box>
  );
};

export const SyncButton = ({ onSync, loading = false }) => {
  const { isConnected, requestSync } = useWebSocket();

  const handleSync = () => {
    if (isConnected) {
      requestSync();
    } else if (onSync) {
      onSync();
    }
  };

  return (
    <Tooltip title={isConnected ? 'Request real-time sync' : 'Manual refresh'}>
      <Chip
        icon={loading ? <SyncDisabled /> : <Sync />}
        label={isConnected ? 'Sync' : 'Refresh'}
        onClick={handleSync}
        disabled={loading}
        color={isConnected ? 'primary' : 'default'}
        variant="outlined"
        clickable
        sx={{
          '& .MuiChip-icon': {
            animation: loading ? 'spin 1s linear infinite' : 'none'
          },
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
          }
        }}
      />
    </Tooltip>
  );
};

export default RealtimeStatusIndicator;
